/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Dialog, DialogActions, DialogContent, DialogHeading } from 'omakase';
import {
  CurrencyContext,
  IWithContextProps,
  LocaleContext,
  consumer,
  core,
  withContext,
} from '@tock/coretypes';
import { Head } from 'head/Head';
import { IntlProvider } from 'react-intl';
import { OfferingDetails } from 'components/business/offering/OfferingDetails';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { StoreType } from '@tock/shared/utils/connect';
import { getFirstExperienceImage } from 'head/experienceImages';
import {
  getRemainingTicketsForEvent,
  isGAEventType,
  isOfferedOnOneDateAndAtOneTime,
  isOfferingEventType,
} from 'utils/offerings';
import { getTitleSuffix } from 'utils/seo';
import { offeringConsumerDetailPageRelativeLink } from '@tock/shared/utils/offerings';
import Anchor from 'components/Anchor';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ExperienceNotFound from 'components/business/search/ExperienceNotFound';
import LoadingComponent from '@tock/shared/components/LoadingComponent';
import Menu, { MENU_CONTAINER_ID } from 'components/business/menu/Menu';
import OfferingsStatusMessage from 'components/business/offering/OfferingsStatusMessage';
import SearchLoading from 'components/business/search/SearchLoading';
import _ from 'lodash';
import queryString from '@tock/query-string';

interface Props {
  business?: consumer.IBusinessConfig;
  calendar: StoreType['calendar'];
  experienceId?: number;
  isAuthenticated: boolean;
  isLoading?: boolean;
  isOpen: boolean;
  isRoute: boolean;
  isWidget: boolean;
  nextRelease: any;
  offerings?: consumer.ConsumerOfferings;
  onClose(): void;
  onAfterClose?(): void;
  onStartOrder?(): void;
  patron?: core.IPatron;
  query?: Dictionary<string>;
}

interface MatchParams {
  businessDomain: string;
  widget?: string;
}

class ExperienceMenuModal extends BaseComponent<
  Props & IWithContextProps & RouteComponentProps<MatchParams>
> {
  widgetPathMod() {
    return this.props.isWidget ? '/widget' : '';
  }
  readQuery() {
    const { location, query } = this.props;
    const queryParams = query ? query : queryString.parse(location.search);

    return {
      cameFrom: queryParams.cameFrom,
    };
  }

  handleBack() {
    window.history.back();
  }

  render() {
    const {
      business,
      match,
      calendar: { calendar, requestInProgress, offeringsInProgress },
      offerings,
      context,
      experienceId,
      isLoading,
      isAuthenticated,
      isOpen,
      isWidget,
      isRoute,
      nextRelease,
      location,
      onClose,
      onAfterClose,
      onStartOrder,
      patron,
    } = this.props;

    const isCalendarLoading = requestInProgress || offeringsInProgress;

    let { cameFrom } = this.readQuery();
    const offering = _.find(
      offerings?.experience as consumer.Offering[],
      (experience) => experience.id === _.toNumber(experienceId)
    );
    const isOfferingEvent = isOfferingEventType(offering);
    if (!BROWSER) {
      cameFrom = cameFrom || '';
    }

    const showLoadingOrErrorState = !experienceId || (isCalendarLoading && !calendar) || !offerings;

    const heading = business?.name ?? '';

    const showBack = cameFrom === 'search_modal' && !isWidget;

    const sharedProps = {
      heading,
      isOpen,
      isRoute,
      isWidget,
      onBack: this.handleBack,
      onClose,
      onAfterClose,
      showBack,
    };
    const isAtOneDateTime = isOfferedOnOneDateAndAtOneTime(offering);
    const useEventCard =
      isAtOneDateTime && (isOfferingEvent || context.isFeatureEnabled('EVENT_LIKE_EXPERIENCES'));
    const remainingEventTickets =
      useEventCard && calendar && offering && offering.id && isGAEventType(offering.type)
        ? getRemainingTicketsForEvent(
            offering.id,
            calendar.ticketGroup as consumer.CalendarTicketGroup[]
          )
        : undefined;

    const offeringName = `${offering?.name ? ' - ' + offering?.name + ' Menu' : ''}`;
    const title = business?.name
      ? `${business.name}${offeringName}${getTitleSuffix(business)}`
      : undefined;
    const description = offering?.description ?? '';

    const resultComponent = (
      <>
        {offering && business ? (
          <OfferingDetails
            business={business}
            offering={offering}
            remainingTickets={remainingEventTickets}
          />
        ) : null}
      </>
    );

    let dialogContent;

    if (showLoadingOrErrorState && isOpen) {
      if (!offering && this.props.staticContext) {
        (this.props.staticContext as any).status = 404;
        (this.props.staticContext as any).soft = true;
      }
      dialogContent = (
        <>
          {resultComponent}
          <SearchLoading
            isAuthenticated={isAuthenticated}
            isError={!experienceId}
            isLoading={isCalendarLoading}
            location={location}
          />
        </>
      );
    }

    const showNoBookingsState =
      (!calendar && !isCalendarLoading) || (!offerings && !isCalendarLoading);
    if (!showLoadingOrErrorState && showNoBookingsState) {
      dialogContent = (
        <>
          {resultComponent}
          <div className="SearchModal-body">
            <OfferingsStatusMessage businessName={business?.name ?? ''} nextRelease={nextRelease} />
          </div>
        </>
      );
    }

    if (!showLoadingOrErrorState && !calendar) {
      dialogContent = (
        <>
          {resultComponent}
          <SearchLoading
            isAuthenticated={isAuthenticated}
            isError={true}
            isLoading={isCalendarLoading}
            location={location}
          />
        </>
      );
    }

    const ticketType = calendar?.ticketType;

    const experience = _.find(
      ticketType as core.TicketType[],
      (e) => e.id === _.toNumber(experienceId)
    );
    const isPrivate = _.includes(offerings?.privateExperienceId, _.toNumber(experienceId));
    if (!showLoadingOrErrorState && !experience) {
      sharedProps.heading = isPrivate
        ? isOfferingEvent
          ? 'Private event'
          : 'Private experience'
        : 'Experience not found';
      dialogContent = (
        <>
          <ExperienceNotFound
            businessEmail={business?.email}
            businessName={business?.name ?? ''}
            isPrivateExperience={isPrivate}
            isWidget={isWidget}
            location={location}
            isAuthenticated={isAuthenticated}
            isOfferingEvent={isOfferingEvent}
            onButtonClick={onClose}
          />
        </>
      );
    }

    const prefix = BROWSER && match.params.widget ? '/widget' : '';
    const experienceLink = experience
      ? this.props.getPath(`${prefix}${offeringConsumerDetailPageRelativeLink(experience)}`)
      : '';

    const isDialogOpen = sharedProps.isOpen;

    if (!showLoadingOrErrorState && experience && business) {
      dialogContent = (
        <IntlProvider locale={(business as consumer.BusinessConfig).locale}>
          <LocaleContext.Provider value={(business as consumer.BusinessConfig).locale}>
            <CurrencyContext.Provider value={(business as consumer.BusinessConfig).currencyCode}>
              {resultComponent}
              <Menu
                ticketType={experience}
                ticketOptionGroup={experience.optionGroup}
                ticketOption={_.flatMap(
                  experience.optionGroup,
                  (group) => group.ticketOption as core.ITicketOption[]
                )}
                optionPrices={_.flatMap(experience.optionGroup, (group) =>
                  _.map(
                    group.ticketOption,
                    (option) =>
                      ({
                        id: option.id,
                        amount: option.defaultPrice,
                      }) as core.IPrice
                  )
                )}
                patron={patron}
              />
            </CurrencyContext.Provider>
          </LocaleContext.Provider>
        </IntlProvider>
      );
    }

    return (
      <>
        {isDialogOpen && (
          <Head
            title={title}
            description={description}
            image={getFirstExperienceImage(offering)}
            site={business?.twitterHandle ?? ''}
            siteName={business?.name ?? ''}
          />
        )}
        <Dialog
          fullWidth
          aria-labelledby="experience-menu-dialog-title"
          aria-describedby={MENU_CONTAINER_ID}
          open={sharedProps.isOpen}
          onClose={sharedProps.onClose}
          data-testid="experience-menu-dialog"
        >
          <DialogHeading id="experience-menu-dialog-title" onClose={sharedProps.onClose}>
            {sharedProps.heading}
          </DialogHeading>
          <DialogContent disablePadding id={MENU_CONTAINER_ID}>
            <LoadingComponent isLoading={isLoading} showOverlay={!isLoading} isBallPulse={true}>
              {dialogContent}
            </LoadingComponent>
          </DialogContent>
          {!showLoadingOrErrorState && experience ? (
            <DialogActions>
              {onStartOrder ? (
                <Button
                  size="large"
                  variant="primary"
                  fullWidth
                  onClick={onStartOrder}
                  data-testid="start-order-button"
                >
                  Start order
                </Button>
              ) : (
                <Button
                  component={Anchor}
                  size="large"
                  variant="primary"
                  fullWidth
                  href={experienceLink}
                  data-testid="start-order-button"
                >
                  Start order
                </Button>
              )}
            </DialogActions>
          ) : null}
        </Dialog>
      </>
    );
  }
}

export default withRouter(withContext(ExperienceMenuModal));
